'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import BgPrivilegeNormal from '../images/bg-privilege-normal.png';
import BgPrivilegeForbid from '../images/bg-privilege-forbid.png';
import BgPrivilegeUp from '../images/bg-privilege-up.png';
import BgPrivilegeIcon from '../images/bg-privilege-coin.png';
import styles from '../index.module.css';
import clsx from 'clsx';
import IconTranslateUp from '../images/icon-translate-up.png';
import IconFont from '@/components/ui/iconFont';
import { getCurrency } from '@/utils/currency';
import { track } from '@/utils/track';

interface PrivilegeContentProps {
  /** 限时转化率，有值则显示限时提升背景图 */
  specialRate?: number | null;
  rateCountdown?: string;
  turnover?: number;
  currency?: string;
  /** 转化状态，禁止时显示禁止背景图 */
  canTranslate: boolean;
  /** 是否已登录 */
  isLogin: boolean;
  /** 当前 VIP 转化率 */
  vipRate?: string | number;
  /** 当前 VIP 最大转化率 */
  vipMaxRate?: string | number;
  handleShowRule: () => void;
}

const bgContainerClass = 'w-full flex-1 bg-cover bg-no-repeat bg-center z-10';

export default function PrivilegeContent({
  specialRate,
  rateCountdown,
  turnover,
  currency,
  canTranslate,
  isLogin,
  vipRate,
  vipMaxRate,
  handleShowRule,
}: PrivilegeContentProps) {
  const t_gullak = useTranslations('gullak');

  const onClickViewRule = () => {
    track('gullak_page_button_click', {
      button_name: 'Rules Middle',
    });
    handleShowRule();
  };

  if (specialRate && canTranslate) {
    return (
      <div
        style={{ backgroundImage: `url(${BgPrivilegeUp.src})` }}
        className={`relative ${bgContainerClass}`}
        onClick={onClickViewRule}
      >
        <div className='absolute top-[50%] -translate-y-1/2 left-[5vw] flex flex-col justify-center'>
          <div className='flex items-center justify-center w-[18vw]'>
            <span
              className={`text-[4.7vw] font-bold mr-1 leading-[1] ${styles['temp-rate-linear']}`}>
              {specialRate}%
            </span>
            <Image src={IconTranslateUp.src} width={12} height={16} alt='up' />
          </div>
          <div className='w-[18vw] text-center text-[#6A7A95] text-[2.6vw] font-medium mt-1 mb-[5px] leading-[9px]'>{t_gullak('gullakRate')}</div>
          {!!rateCountdown && (
            <div className={`
            bg-[rgba(0,0,0,0.2)] rounded-lg flex 
            text-[#BCC3E3] font-medium text-[2.9vw] 
            py-[2px] px-2 leading-[9px] 
            `
            }>
              <div className='min-w-[16vw] whitespace-nowrap'>{rateCountdown}</div>
            </div>
          )}
        </div>
        <div className='absolute top-[50%] -translate-y-1/2 left-[32vw] text-[2.6vw] text-[#74849E] text-left leading-[3.3vw] font-medium w-[59vw]'>
          {t_gullak('specialRateTip', { amount: `${getCurrency(currency || 'INR') + Number(turnover)}` })}
        </div>
        <IconFont name='arrow-right' size={10} color='#00BBFF' className='absolute right-[5vw] top-[50%] -translate-y-1/2' />
      </div>
    );
  }

  if (!canTranslate) {
    return (
      <div style={{ backgroundImage: `url(${BgPrivilegeForbid.src})` }} className={`relative ${bgContainerClass}`} onClick={onClickViewRule}>
        <div className='absolute  top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 flex items-center'>
          <div className='text-[#BCC3E3] font-medium text-[3.4vw] leading-4 whitespace-nowrap'>
            {t_gullak('toBeConvert')}
          </div>
          <IconFont name='arrow-right' size={10} color='#00BBFF' className='ml-3' />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ backgroundImage: `url(${BgPrivilegeNormal.src})` }}
      className={bgContainerClass}
      onClick={onClickViewRule}
    >
      <div className={clsx('h-full flex items-center pl-[5.7vw] relative')}>
        <div className="gullak-rate w-[22.08vw] flex items-center relative">
          <div className="relative mt-2 w-full flex flex-col items-center">
            <div className="relative">
              <Image
                src={BgPrivilegeIcon.src}
                alt="icon-privilege"
                width={116}
                height={42}
                style={{ width: '15.36vw', height: '5.4vw', aspectRatio: '59/21' }}
              />
              {/* 如果已登录，显示当前vip等级转化rate，否则显示当前vip最大的转化rate */}
              <div className={`${styles.rateLinear} absolute left-6 top-[50%] -translate-y-1/2 text-[11px] font-bold`}>
                {isLogin && vipRate ? `${vipRate}%` : '???'}
              </div>
            </div>
            {/* Gullak rate是叠两层实现的效果 */}
            <div className="relative text-[10px] font-semibold">
              <div className={styles.gullakRateText}>{t_gullak('gullakRate')}</div>
              <div className={`${styles.gullakRateTextOverlay} absolute inset-0`}>{t_gullak('gullakRate')}</div>
            </div>
          </div>
        </div>
        <div className="text-[2.6vw] text-[#74849E] mt-1 text-left leading-[3.3vw] font-medium relative w-[59vw] ml-[5vw]">
          {vipRate === vipMaxRate
            ? t_gullak('gullakCashConvertTipKeepVip', { amount: vipMaxRate + '%' })
            : t_gullak('gullakCashConvertTipUpgradeVip', { amount: vipMaxRate + '%' })}
        </div>
        <IconFont name='arrow-right' size={10} color='#00BBFF' className='absolute right-[6.6vw] top-[50%] -translate-y-1/2' />
      </div>
    </div>
  );
}
